<script setup lang="ts">
import Credit from '~/components/credit/Credit.vue'
import SearchResultList from '~/components/header/general-search/SearchResultList.vue'
import Spinner from '~/components/loading/Spinner.vue'
import { useMedia } from '~/composables/useMedia'
import { DEVELOPERS, TRIGGER_WORD } from '~/constants/easter-egg.constants'
import { searchFilterType } from '~/types/search-filter.type'

interface IProps {
  inputValue: string
  resultMap: Map<string, Record<string, any>>
  isFetching: boolean
}

const props = defineProps<IProps>()

const input = toRef(props, 'inputValue')

const isTriggered = computed(() => {
  return input.value === TRIGGER_WORD
})

const showCredit = ref(false)

const { isMobile } = useMedia()

const hasNoResult = computed(() => {
  if (!props.resultMap || !props.resultMap.size) {
    return true
  }
  return !Array.from(props.resultMap.values()).reduce((acc, cur) => {
    return acc + cur.length
  }, 0)
})

const triggerCredit = (value: boolean) => {
  showCredit.value = value
}
</script>

<template>
  <div
    v-if="hasNoResult"
    class="result not-found"
  >
    <Spinner
      v-if="isFetching"
      width="20px"
      height="20px"
    />
    <template v-else-if="isTriggered">
      <span class="text-default/40">{{ DEVELOPERS.length }} Result for</span>
      <button
        class="block font-bold text-primary"
        @click.stop="triggerCredit(true)"
      >
        "{{ TRIGGER_WORD }}"
      </button>
      <Teleport to="body">
        <Credit
          v-if="showCredit"
          @trigger-credit="triggerCredit"
        />
      </Teleport>
    </template>
    <template v-else>
      <span class="text-default/40">No Search Result {{ inputValue && "for" }}</span><span class="block font-bold">{{
        inputValue && `"${input}"`
      }}</span>
    </template>
  </div>
  <div
    v-else
    class="result"
  >
    <SearchResultList
      :type="searchFilterType.TRANSACTION"
      label="TX ID"
      :result-list="resultMap?.get(searchFilterType.TRANSACTION) || []"
    />
    <Divider
      v-if="isMobile && resultMap?.get(searchFilterType.TRANSACTION)?.length && resultMap.get(searchFilterType.BLOCK)?.length"
      class="my-4"
    />
    <SearchResultList
      :type="searchFilterType.BLOCK"
      label="BLOCK"
      :result-list="resultMap?.get(searchFilterType.BLOCK) || []"
    />
    <Divider
      v-if="isMobile && resultMap?.get(searchFilterType.TRANSACTION)?.length && resultMap.get(searchFilterType.ADDRESS)?.length"
      class="my-4"
    />
    <SearchResultList
      :type="searchFilterType.ADDRESS"
      label="ADDRESS"
      :result-list="resultMap?.get(searchFilterType.ADDRESS) || []"
    />
    <SearchResultList
      :type="searchFilterType.MODULE"
      label="MODULE"
      :result-list="resultMap?.get(searchFilterType.MODULE) || []"
    />
  </div>
</template>

<style scoped lang="scss">
.result {
  @apply py-2 border border-default/10 overflow-y-auto flex flex-col gap-y-2 h-fit;

  @include mobile {
    @apply h-full py-6;
  }

  &.not-found {
    @apply justify-center items-center py-24;
  }

  &::-webkit-scrollbar {
    @apply w-2;
  }

  &::-webkit-scrollbar-thumb {
    @apply bg-default/20;
  }
}
</style>